import { withStyles } from '@material-ui/core/styles';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { FacetType, FacetValue, SearchFacet } from 'lib/searchspring/client';
import React, { ReactElement, useState } from 'react';
import ExpandIcon from '../ExpandIcon';
import { handleFilterChange, DUMMY_URL_BASE } from 'lib/product';
import { useRouter } from 'next/router';
import Checkbox from '../Checkbox';
import { useEffect } from 'react';

const MAccordion = withStyles({
  root: {
    boxShadow: 'none',
    borderBottom: '1px solid #BDBDBD',
    '&:last-child': {
      borderBottom: 0,
    },
    '&$expanded': {
      margin: '0px 0px',
      '&:first-child': {
        marginTop: 0,
      },
    },
    '&:before': {
      display: 'none',
    },
  },
  expanded: {
    marginTop: 0,
  },
})(MuiAccordion);

const MAccordionSummary = withStyles({
  root: {
    paddingLeft: 0,
    minHeight: 30,
    '&$expanded': {
      minHeight: 30,
    },
  },
  content: {
    marginTop: 12,
    marginBottom: 0,
    paddingBottom: 4,
    '&$expanded': {
      marginTop: 12,
      marginBottom: 0,
    },
  },
  expanded: {
    marginBottom: 0,
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    marginBottom: -4,
    '& .vertical': {
      transform: 'rotate(0deg)',
    },
    '&$expanded': {
      marginBottom: -4,
      '& .vertical': {
        transform: 'rotate(90deg)',
      },
    },
  },
})(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 0,
    paddingTop: 0,
    marginLeft: 12,
    color: '#828282',
  },
})(MuiAccordionDetails);

export default function MobileFilter({
  facet,
  expanded,
  panelChange,
}: {
  facet: SearchFacet;
  expanded: boolean;
  panelChange: (panel: string) => (event: React.ChangeEvent<unknown>, expanded: boolean) => void;
}): ReactElement {
  const router = useRouter();
  const values = facet.values.sort((a, b) => (a.label >= b.label ? 1 : -1));
  const [focus, setFocus] = useState(0);
  const [options, setOptions] = useState(values);
  const selectedCount = facet.values.filter((value) => value.active).length;
  const [count, setCount] = useState(selectedCount);
  const [showingSubSet, setShowingSubSet] = useState(false);
  const requiresSubSet = facet.values.length > 15;

  const updateFilters = (facetValue: FacetValue): void => {
    const url = handleFilterChange(router.asPath, router.query, facet.field, facetValue);
    const urlObj = new URL(DUMMY_URL_BASE + url);
    const filterParam =
      facetValue.type === FacetType.VALUE ? `filter.${facet.field}` : `filter.${facet.field}.low`;
    setCount(urlObj.searchParams.getAll(filterParam).length);
    router.push(url);
  };

  useEffect(() => {
    setOptions([...facet.values.sort((a, b) => (a.label >= b.label ? 1 : -1))]);
  }, [facet.values]);

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLLIElement>,
    value: FacetValue,
    i: number
  ): void => {
    // Enter or Space
    if (e.keyCode === 13 || e.keyCode === 32) {
      values[i].active = !values[i].active;
      setOptions([...values]);
      updateFilters(value);
    }

    // Down
    if (e.keyCode === 40) {
      if ((e.target as HTMLElement).nextElementSibling) {
        setFocus(focus + 1);
        ((e.target as HTMLElement).nextElementSibling as HTMLElement)?.focus();
      }
    }

    // Up
    if (e.keyCode === 38) {
      if ((e.target as HTMLElement).previousElementSibling) {
        setFocus(focus - 1);
        ((e.target as HTMLElement).previousElementSibling as HTMLElement)?.focus();
      }
    }
  };

  const handleUlKeyPress = (e: React.KeyboardEvent<HTMLUListElement>): void => {
    // Down
    if (e.keyCode === 40) {
      ((e.target as HTMLElement).firstChild as HTMLElement).focus();
    }
  };

  return (
    <MAccordion square expanded={expanded} onChange={panelChange(facet.label)}>
      <MAccordionSummary
        id={`${facet.label}-mobile-filters`}
        expandIcon={<ExpandIcon />}
        IconButtonProps={{ disableRipple: true }}
      >
        <div className="font-bold flex items-center" aria-label={`${facet.label} filter`}>
          {facet.label}
          {count > 0 && (
            <span
              className="ml-2 inline-flex items-center justify-center h-4 w-4 text-white rounded-full bg-tertiary"
              style={{ fontSize: '8px' }}
            >
              {count}
            </span>
          )}
        </div>
      </MAccordionSummary>
      <AccordionDetails>
        <ul
          tabIndex={0}
          role="listbox"
          aria-labelledby={`${facet.label}-mobile-filters`}
          onKeyDown={(e) => handleUlKeyPress(e)}
        >
          {options.map((value, i) => (
            <>
              {i < (showingSubSet ? Infinity : 15) && (
                <li
                  id={`${facet.field}-mobile-filter-${i}`}
                  key={`${value.label}-mobile-filter-${i}`}
                  tabIndex={i === 0 ? 0 : -1}
                  role="option"
                  aria-selected={value.active}
                  onKeyDown={(e) => handleKeyPress(e, value, i)}
                >
                  <Checkbox
                    filter={value}
                    field={facet.field}
                    onChange={(value) => updateFilters(value)}
                    isColor={facet.label === 'Color'}
                  />
                </li>
              )}
            </>
          ))}

          {/* view more  */}
          {requiresSubSet && (
            <button
              aria-label={`${showingSubSet ? 'View More' : 'View Less'} ${facet.label} options`}
              onClick={() => setShowingSubSet(!showingSubSet)}
              className="text-tertiary-actual w-full px-3 py-2 text-left"
            >
              {showingSubSet ? 'View Less' : 'View More'}
            </button>
          )}
        </ul>
      </AccordionDetails>
    </MAccordion>
  );
}
